/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Formats a date for storage in YYYY-MM-DD hh:mm:ss format using Europe/Stockholm timezone
 * This is the standard format used throughout the application for database storage
 * 
 * @param date - The date to format
 * @returns Formatted date string in YYYY-MM-DD hh:mm:ss format in Stockholm timezone
 * 
 * @example
 * formatDateForStorage(new Date()) // "2024-01-15 14:30:25"
 */
export const formatDateForStorage = (date: Date): string => {
    // Convert to Stockholm timezone
    const stockholmDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Stockholm"}));
    const year = stockholmDate.getFullYear();
    const month = String(stockholmDate.getMonth() + 1).padStart(2, '0');
    const day = String(stockholmDate.getDate()).padStart(2, '0');
    const hours = String(stockholmDate.getHours()).padStart(2, '0');
    const minutes = String(stockholmDate.getMinutes()).padStart(2, '0');
    const seconds = String(stockholmDate.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Gets the current date formatted for storage using Stockholm timezone
 * 
 * @returns Current date in YYYY-MM-DD hh:mm:ss format
 */
export const getCurrentDateForStorage = (): string => {
    return formatDateForStorage(new Date());
};

/**
 * Converts a date string in YYYY-MM-DD hh:mm:ss format back to a Date object
 * Assumes the input is in Stockholm timezone
 * 
 * @param dateString - Date string in YYYY-MM-DD hh:mm:ss format
 * @returns Date object
 */
export const parseStorageDate = (dateString: string): Date => {
    // Parse the date string and create a Date object
    // The date string is assumed to be in Stockholm timezone
    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    
    // Create date in Stockholm timezone
    return new Date(year, month - 1, day, hours, minutes, seconds);
};

/**
 * Formats a date for storage in YYYY-MM-DD format using Europe/Stockholm timezone
 * This is used for date-only fields (no time component)
 *
 * @param date - The date to format
 * @returns Formatted date string in YYYY-MM-DD format in Stockholm timezone
 *
 * @example
 * formatDateOnlyForStorage(new Date()) // "2024-01-15"
 */
export const formatDateOnlyForStorage = (date: Date): string => {
    // Convert to Stockholm timezone
    const stockholmDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Stockholm"}));
    const year = stockholmDate.getFullYear();
    const month = String(stockholmDate.getMonth() + 1).padStart(2, '0');
    const day = String(stockholmDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Formats a date string for display (YYYY-MM-DD format)
 *
 * @param dateStr - Date string in storage format (YYYY-MM-DD or YYYY-MM-DD hh:mm:ss)
 * @returns Formatted date string for display (e.g., "2024-01-15") or empty string if no date
 */
export const formatDateForDisplay = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
        // Extract date part only (handles both YYYY-MM-DD and YYYY-MM-DD hh:mm:ss)
        const datePart = dateStr.split(' ')[0];
        return new Date(datePart).toLocaleDateString('sv-SE');
    } catch {
        return '';
    }
};

/**
 * Formats a date string for display with time (full format)
 *
 * @param dateStr - Date string in storage format (YYYY-MM-DD hh:mm:ss)
 * @returns Formatted date and time string for display (e.g., "15 januari 2024 14:30")
 */
export const formatDateTimeForDisplay = (dateStr: string): string => {
    try {
        const date = new Date(dateStr.replace(' ', 'T'));
        return date.toLocaleString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateStr;
    }
};

/**
 * Calculate the number of days until a given due date
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Number of days until the date (negative if overdue)
 *
 * @example
 * getDaysUntilDue('2024-01-20') // 5 (if today is 2024-01-15)
 * getDaysUntilDue('2024-01-10') // -5 (if today is 2024-01-15, overdue)
 */
export const getDaysUntilDue = (dateStr: string): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(dateStr);
    dueDate.setHours(0, 0, 0, 0);
    return Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};


export type DateIntervalUnit = 'days' | 'weeks' | 'months' | 'years';

export interface DateInterval {
    value: number;
    unit: DateIntervalUnit;
}

const daysInMonth = (year: number, monthIndex: number): number =>
    new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Adds an interval to a calendar date and returns the resulting calendar date.
 *
 * Works on the `{year, month, day}` components of the string, never on a local
 * `Date` (no DST or timezone drift). Adding months or years clamps to the last
 * day of the target month: 2026-01-31 + 1 month = 2026-02-28.
 *
 * @param dateStr - 'YYYY-MM-DD' (a trailing ' hh:mm:ss' is ignored)
 * @param interval - value and unit to add
 * @returns 'YYYY-MM-DD'
 */
export const addInterval = (dateStr: string, interval: DateInterval): string => {
    const [year, month, day] = dateStr.split(' ')[0].split('-').map(Number);
    if (!year || !month || !day) {
        throw new Error(`Ogiltigt datum: ${dateStr}`);
    }

    if (interval.unit === 'days' || interval.unit === 'weeks') {
        const days = interval.unit === 'weeks' ? interval.value * 7 : interval.value;
        const shifted = new Date(Date.UTC(year, month - 1, day + days));
        return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
    }

    const monthsToAdd = interval.unit === 'years' ? interval.value * 12 : interval.value;
    const totalMonths = (month - 1) + monthsToAdd;
    const targetYear = year + Math.floor(totalMonths / 12);
    const targetMonthIndex = ((totalMonths % 12) + 12) % 12;
    const targetDay = Math.min(day, daysInMonth(targetYear, targetMonthIndex));
    return `${targetYear}-${pad2(targetMonthIndex + 1)}-${pad2(targetDay)}`;
};
